import { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from 'context/AuthContext';
import NotificationBell from 'components/NotificationBell';
import useTablasDeslizables from './useTablasDeslizables';
import eventifyLogo from 'assets/eventify-logo.png';
import styles from './AdminLayout.module.css';

const superAdminNav = [
  { to: '/dashboard', icon: 'fa-house', label: 'Dashboard', exact: true },
  { section: 'Propiedades' },
  { to: '/admin/propiedades',          icon: 'fa-building',        label: 'Propiedades' },
  { to: '/admin/torres',               icon: 'fa-layer-group',     label: 'Torres' },
  { to: '/admin/unidades',             icon: 'fa-door-open',       label: 'Unidades' },
  { to: '/admin/propietarios-import',  icon: 'fa-users',           label: 'Propietarios' },
  { to: '/admin/comunicados',          icon: 'fa-bullhorn',        label: 'Comunicados' },
  { to: '/admin/administradores',      icon: 'fa-user-shield',     label: 'Administradores' },
  { section: 'Gestión De Asambleas' },
  { to: '/admin/asambleas',            icon: 'fa-gavel',           label: 'Asambleas' },
  { to: '/admin/preguntas',            icon: 'fa-circle-question', label: 'Preguntas' },
  { to: '/admin/asistentes',           icon: 'fa-clipboard-list',  label: 'Asistentes' },
  { section: 'Reportes' },
  { to: '/admin/reportes/actas',        icon: 'fa-file-pdf',   label: 'Actas de asamblea' },
  { to: '/admin/reportes/cuentas',       icon: 'fa-address-book',    label: 'Cuentas de propietarios' },
  { to: '/admin/reportes/importaciones', icon: 'fa-file-excel', label: 'Reportes de importación' },
  { section: 'Eventos y Logística' },
  { to: '/admin/companias',            icon: 'fa-briefcase',       label: 'Compañías' },
  { to: '/admin/eventos',              icon: 'fa-calendar-days',   label: 'Eventos' },
  { to: '/admin/invitados',            icon: 'fa-id-badge',        label: 'Invitados' },
  { to: '/admin/reportes/eventos',     icon: 'fa-file-lines',      label: 'Reportes' },
  { section: 'Administración' },
  { to: '/admin/usuarios',             icon: 'fa-user',            label: 'Usuarios' },
  { to: '/admin/membresias',           icon: 'fa-id-card',         label: 'Membresías' },
  { to: '/admin/glosario',             icon: 'fa-book',            label: 'Glosario' },
];

const adminNav = [
  { to: '/dashboard', icon: 'fa-house', label: 'Dashboard', exact: true },
  { section: 'Mi propiedad' },
  { to: '/admin/propiedades',         icon: 'fa-building',    label: 'Mi propiedad' },
  { to: '/admin/torres',              icon: 'fa-layer-group', label: 'Torres' },
  { to: '/admin/unidades',            icon: 'fa-door-open',   label: 'Unidades' },
  { to: '/admin/propietarios-import', icon: 'fa-users',       label: 'Propietarios' },
  { to: '/admin/comunicados',         icon: 'fa-bullhorn',    label: 'Comunicados' },
  { section: 'Asambleas' },
  { to: '/admin/asambleas',   icon: 'fa-gavel',            label: 'Asambleas' },
  { to: '/admin/preguntas',   icon: 'fa-circle-question',  label: 'Preguntas' },
  { to: '/admin/asistentes',  icon: 'fa-clipboard-list',   label: 'Asistentes' },
  { section: 'Reportes' },
  { to: '/admin/reportes/actas',         icon: 'fa-file-pdf',   label: 'Actas de asamblea' },
  { to: '/admin/reportes/cuentas',       icon: 'fa-address-book',    label: 'Cuentas de propietarios' },
  { to: '/admin/reportes/importaciones', icon: 'fa-file-excel', label: 'Reportes de importación' },
  { section: 'Referencia' },
  { to: '/admin/glosario',               icon: 'fa-book',       label: 'Glosario' },
];

// Nav del administrador de asamblea — solo lo que puede gestionar
const assemblyAdminNav = [
  { to: '/dashboard', icon: 'fa-house', label: 'Mi Asamblea', exact: true },
  { section: 'Gestión de Asamblea' },
  { to: '/admin/asistentes',  icon: 'fa-clipboard-list',  label: 'Asistentes' },
  { to: '/admin/preguntas',   icon: 'fa-circle-question', label: 'Preguntas' },
  { to: '/admin/comunicados', icon: 'fa-bullhorn',        label: 'Comunicados' },
  { section: 'Referencia' },
  { to: '/admin/glosario',    icon: 'fa-book',            label: 'Glosario' },
];

// Nav plan Asamblea + Logística — gestión de propiedad sin asambleas
const logisticaAdminNav = [
  { to: '/dashboard', icon: 'fa-house', label: 'Dashboard', exact: true },
  { section: 'Mi propiedad' },
  { to: '/admin/propiedades',         icon: 'fa-building',    label: 'Mi propiedad' },
  { to: '/admin/torres',              icon: 'fa-layer-group', label: 'Torres' },
  { to: '/admin/unidades',            icon: 'fa-door-open',   label: 'Unidades' },
  { to: '/admin/propietarios-import', icon: 'fa-users',       label: 'Propietarios' },
  { section: 'Reportes' },
  // El plan Asamblea + Logística no incluye la gestión de asambleas ni los
  // comunicados (esos servicios los habilita el plan) — sí ver/descargar actas
  // ya existentes (de cuando la propiedad tenía otro plan, o registradas por
  // super_admin).
  { to: '/admin/reportes/actas',         icon: 'fa-file-pdf',   label: 'Actas de asamblea' },
  { to: '/admin/reportes/cuentas',       icon: 'fa-address-book',    label: 'Cuentas de propietarios' },
  { to: '/admin/reportes/importaciones', icon: 'fa-file-excel', label: 'Reportes de importación' },
  { section: 'Referencia' },
  { to: '/admin/glosario',               icon: 'fa-book',       label: 'Glosario' },
];

const userNav = [
  { to: '/dashboard',      icon: 'fa-house',     label: 'Dashboard', exact: true },
  { section: 'Mi cuenta' },
  { to: '/admin/unidades', icon: 'fa-door-open', label: 'Mis unidades' },
  { to: '/admin/perfil',   icon: 'fa-user',      label: 'Mi perfil' },
];

// F-18: el representante EXTERNO no es propietario — sin "Mis unidades" ni
// "Mi perfil" (su cuenta es temporal, se borra al finalizar la asamblea).
const externoNav = [
  { to: '/dashboard', icon: 'fa-house', label: 'Dashboard', exact: true },
];

export default function AdminLayout() {
  const { user, logout, isSuperAdmin, isAdmin, isAssemblyAdmin, isLogisticaAdmin, isTrial } = useAuth();
  const navigate = useNavigate();

  const navItems = isSuperAdmin      ? superAdminNav
                 : isAssemblyAdmin   ? assemblyAdminNav
                 : isLogisticaAdmin  ? logisticaAdminNav
                 : isAdmin           ? adminNav
                 : user?.is_temporary_rep ? externoNav : userNav;

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  useTablasDeslizables();

  // Cerrar sidebar al navegar (mobile)
  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  // Cerrar sidebar al hacer resize a desktop
  useEffect(() => {
    const onResize = () => { if (window.innerWidth > 768) setSidebarOpen(false); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const handleLogout = async () => { await logout(); navigate('/login'); };

  // Badge de rol para el topbar
  const roleBadge = isLogisticaAdmin ? { label: 'Asamblea + Logística', color: '#059669' }
               : isAssemblyAdmin
    ? { label: 'Admin Asamblea', color: '#6c63ff' }
    : null;

  return (
    <div className="app-layout">
      {/* Overlay oscuro — cierra el sidebar al tocar fuera (mobile) */}
      <div
        className={`sidebar-overlay${sidebarOpen ? ' visible' : ''}`}
        onClick={() => setSidebarOpen(false)}
      />

      <aside className={`sidebar${sidebarOpen ? ' open' : ''}`}>
        <div className="sidebar-logo">
          <div className={styles.logoCaja}>
            <img src={eventifyLogo} alt="EVENTIFY" className={`auth-logo-img ${styles.logoImg}`} />
          </div>
        </div>

        <nav className={`sidebar-nav ${styles.navegacion}`}>
          {navItems.map((item, i) =>
            item.section ? (
              <div key={i} className="nav-section-title">{item.section}</div>
            ) : (
              <NavLink key={item.to} to={item.to} end={item.exact}
                className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>
                <i className={`fas ${item.icon}`} />
                {item.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="sidebar-footer">
          <button className="nav-item logout-btn" onClick={handleLogout}>
            <i className="fas fa-right-from-bracket" /> Cerrar sesión
          </button>
        </div>
      </aside>

      <div className="main-content">
        {/* Banner modo sandbox para admin trial */}
        {isTrial && (
          <div className={styles.bannerPrueba}>
            <i className={`fas fa-flask ${styles.bannerIcono}`} />
            Modo de prueba — Las asambleas creadas son temporales y se eliminarán al expirar el periodo de prueba.
            Los propietarios no recibirán correos ni verán estas asambleas.
            <span className={styles.bannerPlan}>
              <i className={`fas fa-clock ${styles.bannerRelojIcono}`} />Plan Trial activo
            </span>
          </div>
        )}
        <header className="topbar">
          {/* Botón hamburguesa — solo visible en mobile */}
          <button
            className="hamburger-btn"
            onClick={() => setSidebarOpen(o => !o)}
            aria-label="Menú"
          >
            <i className={`fas ${sidebarOpen ? 'fa-times' : 'fa-bars'}`} />
          </button>
          <span className="topbar-title">
            <i className={`fas fa-circle ${styles.puntoActivo}`} />
            Sistema activo
            {roleBadge && (
              <span className={`topbar-rol ${styles.chipRol}`} style={{ '--tono': roleBadge.color, '--fondo': roleBadge.color + '22' }}>
                {roleBadge.label}
              </span>
            )}
          </span>
          <div className={`topbar-user ${styles.usuario}`}>
            {user?.role === 'user' && !user?.is_temporary_rep && <NotificationBell />}
            <NavLink to="/admin/perfil" className={styles.enlacePerfil}>
              <span>{user?.name}</span>
              <div className="avatar">{user?.name?.[0]?.toUpperCase()}</div>
            </NavLink>
          </div>
        </header>
        <main className="page-content"><Outlet /></main>
      </div>
    </div>
  );
}